import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';
import { Messages } from '@dojo/framework/i18n/i18n';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import Button from '@dojo/widgets/button';
import { CountdownTimer } from '../widgets/countdown-timer';

import { lccstore } from 'lcc-state';

import { QuizMeta, Question } from '../interfaces';

import css from '../css/prometric-review.m.css';

export interface PrometricMPEReviewProperties {
	quizstore: Store<QuizMeta>;
	isaudit: boolean;
	loaded?: boolean;
	localizedBundle: { isPlaceholder: boolean; format: any; messages: Messages };

	getQuestions: () => Question[];
	onFinish: () => void;
	onReview: (flag: boolean) => void;
	filterOn: (name: 'marked' | 'complete' | 'incomplete' | 'all' | number) => void;
	checkFor: (name: 'marked' | 'complete' | 'incomplete' | 'all') => boolean;
	hasNextSection: () => boolean;
}

@theme(css)
export class PrometricMPEReview extends I18nThemed<PrometricMPEReviewProperties> {
	private areyousure: boolean = false;
	private data: Question[] = [];
	private _inited: boolean = false;
	private _inFinish = false;

	protected onAttach(): void {
		lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
	}

	@beforeProperties()
	ensure_nls(
		properties: PrometricMPEReviewProperties & WidgetProperties
	): PrometricMPEReviewProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		(() => {
			const { quizstore } = properties;
			const { get, path } = quizstore;
			properties.loaded = get(path('loaded'));
			if (!this._inited) {
				const invalidatePaths = [['loaded']].map((path: any) => quizstore.path(path.join('/')));
				const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
				this.own({
					destroy: () => {
						handle.remove();
					}
				});
				this._inited = true;
			}
		})();
		return properties;
	}

	protected render_heads(): DNode {
		const {
			isaudit,
			localizedBundle: {
				messages: {
					question: questionText = '',
					incorrect: incorrectText = '',
					marked: markedText = '',
					status: statusText = ''
				}
			}
		} = this.properties;

		return v(
			'div',
			{ key: 'head-block', role: 'rowgroup', classes: [this.theme(css.tableHead), this.theme(css.row)] },
			[
				v('div', { role: 'row' }, [
					v(
						'div',
						{
							key: 'head-question',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.question)]
						},
						[questionText]
					),
					v(
						'div',
						{
							key: 'head-marked',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.marked)]
						},
						[isaudit ? incorrectText : markedText]
					),
					v(
						'div',
						{
							key: 'head-status',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.status)]
						},
						[statusText]
					)
				])
			]
		);
	}

	protected render_row(question: Question): DNode {
		const {
			isaudit,
			filterOn,
			onReview,
			localizedBundle: {
				messages: {
					question: questionText = '',
					incorrect: incorrectText = '',
					marked: markedText = '',
					status_complete = '[quiz] status_complete',
					status_incomplete = '[quiz] status_incomplete'
				}
			}
		} = this.properties;
		const { cursor, marked = false, flags } = question;
		const isOdd = !!(cursor % 2);
		const isComplete = flags[1];
		const isMarked = marked;
		return v(
			'a',
			{
				key: `row-${cursor}`,
				role: 'row',
				href: 'javascript:void;',
				classes: [this.theme(css.row), isOdd ? this.theme(css.oddRow) : null],
				onclick: (event: any) => {
					event && event.stopPropagation();
					filterOn(cursor);
					onReview(false);
				}
			},
			[
				v('div', { key: `q-${cursor}`, role: 'cell', classes: [this.theme(css.question)] }, [
					`${questionText} ${cursor + 1}`
				]),
				v(
					'div',
					{
						id: isaudit ? incorrectText : markedText,
						key: `mrk-${cursor}`,
						role: 'cell',
						classes: [this.theme(css.marked)]
					},
					[
						isMarked
							? v('i', {
									...formatAriaProperties({ labelledBy: isaudit ? incorrectText : markedText }),
									classes: ['fa', 'fa-check']
							  })
							: v('i', {}, [' '])
					]
				),
				v(
					'div',
					{
						key: `cmp-${cursor}`,
						role: 'cell button',
						classes: [
							this.theme(css.status),
							isComplete ? this.theme(css.complete) : this.theme(css.incomplete)
						]
					},
					[
						isComplete
							? v('i', {
									...formatAriaProperties({ label: status_complete }),
									classes: ['fa', 'fa-circle']
							  })
							: v('i', {
									...formatAriaProperties({ label: status_incomplete }),
									classes: ['fa', 'fa-exclamation-circle']
							  })
					]
				)
			]
		);
	}

	protected render_buttons(): DNode[] {
		const {
			quizstore,
			onFinish,
			filterOn,
			checkFor,
			onReview,
			isaudit,
			localizedBundle: {
				messages: {
					review_all = '',
					review_incorrect = '',
					review_marked = '',
					review_incomplete = '',
					end_review = ''
				}
			}
		} = this.properties;

		return [
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'all',
						onClick: () => {
							filterOn('all');
							onReview(false);
						}
					},
					[review_all]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'marked',
						disabled: !checkFor('marked'),
						onClick: () => {
							filterOn('marked');
							onReview(false);
						}
					},
					[isaudit ? review_incorrect : review_marked]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'incomplete',
						disabled: !checkFor('incomplete'),
						onClick: () => {
							filterOn('incomplete');
							onReview(false);
						}
					},
					[review_incomplete]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'finish',
						onClick: () => {
							if (isaudit) {
								onFinish();
							} else {
								this.areyousure = true;
								this.invalidate();
							}
						}
					},
					[end_review]
				)
			]),
			v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [
				!isaudit
					? v('div', { classes: [this.theme(css.timer)] }, [w(CountdownTimer, { quizstore, countdown: '' })])
					: null
			])
		];
	}

	protected areYouSure(): DNode[] {
		const {
			onFinish,
			hasNextSection,
			localizedBundle: {
				messages: {
					ays_title = '',
					ays_body = '',
					ays_section = 'Are you sure you want to submit this section of the exam? You will not be able to return to this section once you submit.',
					ays_cancel = '',
					submit: submitText = ''
				}
			}
		} = this.properties;
		const nextSection = hasNextSection();

		return [
			v('div', { xqa: 'are-you-sure', classes: [this.theme(css.areYouSure)] }, [
				v('h2', { classes: [this.theme(css.sureHeading)] }, [ays_title]),
				v(
					'div',
					{ key: '1', xqa: `${nextSection ? 'section' : 'quiz'}-end`, classes: [this.theme(css.sureBody)] },
					[nextSection ? ays_section : ays_body]
				),
				v('div', { key: '2', classes: [this.theme(css.sureButtons)] }, [
					w(
						Button,
						{
							key: 'ays-cancel',
							onClick: () => {
								this.areyousure = false;
								this.invalidate();
							}
						},
						[ays_cancel]
					),
					w(
						Button,
						{
							key: 'ays-finish',
							onClick: () => {
								if (!this._inFinish) {
									this._inFinish = true;
									onFinish();
								}
							}
						},
						[submitText]
					)
				])
			])
		];
	}

	protected render_review(): DNode[] {
		const {
			getQuestions,
			loaded = false,
			localizedBundle: {
				messages: { review_instructions = '' }
			}
		} = this.properties;

		if (!loaded) {
			v('div', { key: 'loading', classes: [this.theme(css.loading)] }, [
				v('i', { classes: ['fas', 'fa-spinner', 'fa-spin'] })
			]);
		}

		if (this.data.length === 0) {
			this.data = getQuestions();
		}
		const { data } = this;
		const kids = data.map<DNode>((q) => this.render_row(q));

		return [
			v('div', { key: 'buttons', classes: [this.theme(css.buttons)] }, this.render_buttons()),
			v('div', { key: 'instruct', classes: [this.theme(css.instruct)] }, [review_instructions]),
			v('div', { key: 'review-table', role: 'table', classes: [this.theme(css.table)], tabindex: '0' }, [
				this.render_heads(),
				v('div', { role: 'rowgroup' }, [...kids])
			])
		];
	}

	protected render(): DNode {
		const { isPlaceholder } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return null;
		}
		const { areyousure } = this;
		const { isaudit } = this.properties;
		const xqa = `review ${isaudit ? 'audit' : 'quiz'}`;

		return v(
			'div',
			{ key: 'review', xqa, classes: [this.theme(css.root)] },
			areyousure ? this.areYouSure() : this.render_review()
		);
	}
}

export default PrometricMPEReview;
